import { useEffect, useState, useCallback } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useAuth } from "../contexts/AuthContext";
import RoleBasedLayout, {
  type LayoutMenuItem,
} from "../components/RoleBasedLayout";
import {
  LayoutDashboard,
  Building,
  Store,
  DollarSign,
  Megaphone,
} from "lucide-react";
import Setting from "./institution/Settings";

import Dashboard from "./institution/Dashboard";
import Institutions from "./institution/Institutions";
import VendorList from "./institution/VendorList";
import DonationCampaignsPage from "./super-admin/DonationCampaigns";
import _Students from "./institution/Students";
import _Attendance from "./institution/Attendance";
import Documents from "./institution/Documents";
import Reports from "./institution/Reports";
import Profile from "./institution/Profile";
import { institutionsAPI } from "../services/api";

const initialMenuItems: LayoutMenuItem[] = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/institution-admin/dashboard",
  },
  {
    icon: Building,
    label: "My Institution",
    path: "/institution-admin/institutions",
  },
  {
    icon: Store,
    label: "Vendors",
    path: "/institution-admin/vendors",
  },
  {
    icon: DollarSign,
    label: "Donation",
    path: "/institution-admin/donation-campaigns",
    subItems: [
      {
        label: "Donation Campaigns",
        path: "/institution-admin/donation-campaigns",
        icon: Megaphone,
      },
    ],
  },
  // { icon: Users, label: "Students", path: "/institution-admin/students" },
  // {
  //   icon: ClipboardList,
  //   label: "Attendance",
  //   path: "/institution-admin/attendance",
  // },
  // { icon: FileText, label: "Documents", path: "/institution-admin/documents" },
  // { icon: BarChart3, label: "Reports", path: "/institution-admin/reports" },
  // { icon: Settings, label: "Settings", path: "/institution-admin/settings" },
  // { icon: User, label: "Profile", path: "/institution-admin/profile" },
];

export default function InstitutionAdminDashboard() {
  const { user, userRole, loading: authLoading } = useAuth();
  const navigate = usePrefixedNavigate();
  const [institutionStatus, setInstitutionStatus] = useState<string | null>(
    null
  );
  const [fetchingStatus, setFetchingStatus] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!user) {
      return;
    }
    try {
      const raw = await institutionsAPI.getAll();
      let list: any[] = [];
      if (Array.isArray(raw)) {
        list = raw;
      } else if (raw && typeof raw === "object") {
        const inner = (raw as any).data;
        if (Array.isArray(inner)) {
          list = inner;
        } else if (inner && typeof inner === "object") {
          list = [inner];
        } else {
          list = (raw as any).results ?? [];
        }
      }
      const inst = list[0];
      if (inst) {
        setInstitutionStatus(inst.status ?? inst.approval_status ?? null);
      }
    } catch (error) {
      console.error("Error fetching institution status:", error);
    } finally {
      setFetchingStatus(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && userRole === "institutions_admin") {
      fetchStatus();
    } else if (!authLoading) {
      setFetchingStatus(false);
    }
  }, [user, userRole, authLoading, fetchStatus]);

  // Only login + role control access. No other API (e.g. institution check) redirects away.
  useEffect(() => {
    if (!authLoading && userRole && userRole !== "institutions_admin") {
      navigate("/jainboarding-admin/auth?portal=institution");
    }
  }, [userRole, authLoading, navigate]);

  if (authLoading || fetchingStatus) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  const isRestricted =
    !institutionStatus ||
    institutionStatus.toLowerCase() === "pending" ||
    institutionStatus.toLowerCase() === "unknown";

  const menuItems = initialMenuItems.map(item => ({
    ...item,
    disabled:
      isRestricted &&
      item.label !== "Dashboard" &&
      item.label !== "My Institution",
  }));

  return (
    <RoleBasedLayout
      menuItems={menuItems}
      sidebarTitle="JBF Portal"
      sidebarSubtitle="Institution Admin"
    >
      <Routes>
        <Route
          path="/"
          element={<Navigate to="/institution-admin/institutions" replace />}
        />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/institutions" element={<Institutions />} />
        <Route path="/vendors" element={<VendorList />} />
        <Route path="/donation-campaigns" element={<DonationCampaignsPage />} />
        {/* <Route path="/students" element={<Students />} />
        <Route path="/attendance" element={<Attendance />} /> */}
        <Route path="/documents" element={<Documents />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/settings" element={<Setting />} />
        <Route path="/profile" element={<Profile />} />
      </Routes>
    </RoleBasedLayout>
  );
}
